<template>
  <div
    class="mobile-shell"
    :class="{
      'is-desktop': isDesktop,
      'sidebar-collapsed': isDesktop && sidebarCollapsed,
      'sidebar-resizing': sidebarResizing,
    }"
  >
    <nav
      v-show="isDesktop || !todoDetail"
      class="mobile-shell-nav"
      :aria-label="isDesktop ? 'main' : 'mobile'"
      :style="desktopNavStyle"
    >
      <button
        type="button"
        :class="{ active: tab === 'calendar' }"
        :title="$t('settings.calendar')"
        @click="goTab('calendar')"
      >
        <i class="bi-calendar3" aria-hidden="true"></i>
        <span class="mobile-shell-nav-label">{{ $t("settings.calendar") }}</span>
      </button>
      <button
        type="button"
        v-if="showCustomList"
        :class="{ active: tab === 'lists' }"
        :title="$t('settings.customLists')"
        @click="goTab('lists')"
      >
        <i class="bi-list-ul" aria-hidden="true"></i>
        <span class="mobile-shell-nav-label">{{ $t("settings.customLists") }}</span>
      </button>
      <button
        type="button"
        :class="{ active: tab === 'notes' }"
        :title="$t('memos.title')"
        @click="goTab('notes')"
      >
        <i class="bi-journal-text" aria-hidden="true"></i>
        <span class="mobile-shell-nav-label">{{ $t("memos.title") }}</span>
      </button>
      <button
        type="button"
        :class="{ active: tab === 'more' }"
        :title="$t('ui.more')"
        @click="goTab('more')"
      >
        <i class="bi-three-dots" aria-hidden="true"></i>
        <span class="mobile-shell-nav-label">{{ $t("ui.more") }}</span>
      </button>

      <button
        v-if="isDesktop"
        type="button"
        class="mobile-shell-collapse"
        :aria-pressed="sidebarCollapsed"
        :aria-label="sidebarCollapsed ? '展开边栏' : '折叠边栏'"
        :title="sidebarCollapsed ? '展开边栏' : '折叠边栏'"
        @click="toggleSidebarCollapsed"
      >
        <i :class="sidebarCollapsed ? 'bi-chevron-right' : 'bi-chevron-left'" aria-hidden="true"></i>
        <span class="mobile-shell-nav-label">{{ sidebarCollapsed ? '展开' : '折叠' }}</span>
      </button>

      <div
        v-if="isDesktop"
        class="mobile-shell-resizer"
        role="separator"
        aria-orientation="vertical"
        aria-label="调整边栏宽度"
        tabindex="0"
        @mousedown.prevent="onResizeStart"
        @touchstart.prevent="onResizeStart"
        @keydown="onResizerKeydown"
      ></div>
    </nav>
    <main
      class="mobile-shell-main"
      :class="{
        'has-todo-detail': !!todoDetail && !isDesktop,
        'has-notes-focus': !!notesFocus,
      }"
    >
      <div v-show="(!todoDetail || isDesktop) && !notesFocus" class="mobile-shell-main-tabs">
        <mobile-calendar-page
          v-if="tab === 'calendar'"
          :selected-day="selectedDay"
          @update:selected-day="onSelectedDayUpdate"
        />
        <mobile-custom-lists-page v-else-if="tab === 'lists'" />
        <mobile-notes-page v-else-if="tab === 'notes'" />
        <admin-users-page
          v-else-if="tab === 'admin'"
          @back="backFromAdmin"
        />
        <mobile-more-menu
          v-else
          :is-admin="isAdmin"
          :app-version="appVersion"
          @open-admin="goTab('admin')"
        />
      </div>
      <mobile-notes-focus-page v-if="notesFocus" :focus="notesFocus" />
      <mobile-todo-detail-page
        v-if="todoDetail && !isDesktop && !notesFocus"
        :list-id="todoDetail.listId"
        :todo-index="todoDetail.index"
      />
    </main>
  </div>
</template>

<script>
import MobileCalendarPage from "./MobileCalendarPage.vue";
import MobileCustomListsPage from "./MobileCustomListsPage.vue";
import MobileNotesPage from "./MobileNotesPage.vue";
import MobileNotesFocusPage from "./MobileNotesFocusPage.vue";
import MobileMoreMenu from "./MobileMoreMenu.vue";
import AdminUsersPage from "./AdminUsersPage.vue";
import MobileTodoDetailPage from "./MobileTodoDetailPage.vue";
const { subscribeMobileViewport } = require("./viewport.js");
const { subscribeTodoDetail, closeTodoDetail } = require("./mobileNav.js");
const { subscribeNotesFocus } = require("./notesNav.js");
const { selectTab, subscribeTab, getTab } = require("./shellNav.js");
const {
  SIDEBAR_DEFAULT,
  clampSidebarWidth,
  sidebarDisplayWidth,
  shouldCollapseFromDrag,
  loadSidebarPrefs,
  saveSidebarPrefs,
} = require("./sidebarLayout.js");
import { subscribeMobileKeyboardLayout } from "../helpers/mobileViewport.js";

export default {
  name: "MobileShell",
  components: {
    MobileCalendarPage,
    MobileCustomListsPage,
    MobileNotesPage,
    MobileNotesFocusPage,
    MobileMoreMenu,
    AdminUsersPage,
    MobileTodoDetailPage,
  },
  props: {
    selectedDay: {
      type: String,
      default: null,
    },
    isAdmin: {
      type: Boolean,
      default: false,
    },
    appVersion: {
      type: String,
      default: "",
    },
  },
  emits: ["update:selectedDay"],
  data() {
    const prefs =
      typeof localStorage !== "undefined"
        ? loadSidebarPrefs(localStorage)
        : { width: SIDEBAR_DEFAULT, collapsed: false };
    return {
      tab: typeof window !== "undefined" ? getTab() : "calendar",
      isDesktop: typeof window !== "undefined" ? window.innerWidth >= 768 : false,
      todoDetail: null,
      notesFocus: null,
      unsubscribeViewport: null,
      unsubscribeKeyboard: null,
      unsubscribeTodoDetail: null,
      unsubscribeNotesFocus: null,
      unsubscribeTab: null,
      onWindowResize: null,
      sidebarWidth: prefs.width,
      sidebarCollapsed: prefs.collapsed,
      sidebarResizing: false,
      resizeStartX: 0,
      resizeStartWidth: 0,
    };
  },
  computed: {
    showCustomList() {
      return this.$store.getters.config.customList;
    },
    desktopNavStyle() {
      if (!this.isDesktop) return undefined;
      const width = sidebarDisplayWidth(this.sidebarWidth, this.sidebarCollapsed);
      return {
        width: `${width}px`,
        flexBasis: `${width}px`,
      };
    },
  },
  watch: {
    showCustomList(enabled) {
      if (!enabled && this.tab === "lists") {
        this.goTab("calendar");
      }
    },
  },
  mounted() {
    this.unsubscribeViewport = subscribeMobileViewport((isPhone) => {
      this.isDesktop = !isPhone;
      if (!isPhone && this.todoDetail) closeTodoDetail();
    });
    const onResize = () => {
      const desktop = window.innerWidth >= 768;
      this.isDesktop = desktop;
      if (desktop && this.todoDetail) closeTodoDetail();
    };
    this.onWindowResize = onResize;
    window.addEventListener("resize", onResize);
    // 虚拟键盘：同步可视视口高度，并把焦点输入滚进可见区
    this.unsubscribeKeyboard = subscribeMobileKeyboardLayout();
    this.unsubscribeTodoDetail = subscribeTodoDetail((detail) => {
      this.todoDetail = detail;
    });
    this.unsubscribeNotesFocus = subscribeNotesFocus((focus) => {
      this.notesFocus = focus;
      if (focus) this.tab = "notes";
    });
    this.unsubscribeTab = subscribeTab((tab) => {
      this.tab = tab;
    });
    selectTab(this.tab, { replace: true });
  },
  beforeUnmount() {
    if (this.unsubscribeViewport) this.unsubscribeViewport();
    if (this.unsubscribeKeyboard) this.unsubscribeKeyboard();
    if (this.unsubscribeTodoDetail) this.unsubscribeTodoDetail();
    if (this.unsubscribeNotesFocus) this.unsubscribeNotesFocus();
    if (this.unsubscribeTab) this.unsubscribeTab();
    if (this.onWindowResize) window.removeEventListener("resize", this.onWindowResize);
    this.stopResizeListeners();
  },
  methods: {
    goTab(tab) {
      selectTab(tab);
    },
    backFromAdmin() {
      if (typeof window !== "undefined" && window.history.length > 1) {
        window.history.back();
        return;
      }
      selectTab("more");
    },
    onSelectedDayUpdate(day) {
      this.$emit("update:selectedDay", day);
    },
    persistSidebar() {
      if (typeof localStorage === "undefined") return;
      saveSidebarPrefs(localStorage, {
        width: this.sidebarWidth,
        collapsed: this.sidebarCollapsed,
      });
    },
    toggleSidebarCollapsed() {
      this.sidebarCollapsed = !this.sidebarCollapsed;
      if (!this.sidebarCollapsed) {
        this.sidebarWidth = clampSidebarWidth(this.sidebarWidth);
      }
      this.persistSidebar();
    },
    onResizeStart(event) {
      if (!this.isDesktop) return;
      const point = event.touches && event.touches[0] ? event.touches[0] : event;
      this.sidebarResizing = true;
      this.resizeStartX = point.clientX;
      this.resizeStartWidth = sidebarDisplayWidth(this.sidebarWidth, this.sidebarCollapsed);
      if (this.sidebarCollapsed) {
        this.sidebarCollapsed = false;
        this.sidebarWidth = clampSidebarWidth(this.resizeStartWidth);
      }
      window.addEventListener("mousemove", this.onResizeMove);
      window.addEventListener("mouseup", this.onResizeEnd);
      window.addEventListener("touchmove", this.onResizeMove, { passive: false });
      window.addEventListener("touchend", this.onResizeEnd);
    },
    onResizeMove(event) {
      if (!this.sidebarResizing) return;
      if (event.cancelable && event.preventDefault) event.preventDefault();
      const point = event.touches && event.touches[0] ? event.touches[0] : event;
      const next = this.resizeStartWidth + (point.clientX - this.resizeStartX);
      if (shouldCollapseFromDrag(next)) {
        this.sidebarCollapsed = true;
        return;
      }
      this.sidebarCollapsed = false;
      this.sidebarWidth = clampSidebarWidth(next);
    },
    onResizeEnd() {
      if (!this.sidebarResizing) return;
      this.sidebarResizing = false;
      this.stopResizeListeners();
      if (!this.sidebarCollapsed) {
        this.sidebarWidth = clampSidebarWidth(this.sidebarWidth);
      }
      this.persistSidebar();
    },
    stopResizeListeners() {
      window.removeEventListener("mousemove", this.onResizeMove);
      window.removeEventListener("mouseup", this.onResizeEnd);
      window.removeEventListener("touchmove", this.onResizeMove);
      window.removeEventListener("touchend", this.onResizeEnd);
    },
    onResizerKeydown(event) {
      if (!this.isDesktop) return;
      const step = event.shiftKey ? 24 : 12;
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        if (this.sidebarCollapsed) return;
        const next = this.sidebarWidth - step;
        if (shouldCollapseFromDrag(next)) {
          this.sidebarCollapsed = true;
        } else {
          this.sidebarWidth = clampSidebarWidth(next);
        }
        this.persistSidebar();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        if (this.sidebarCollapsed) {
          this.sidebarCollapsed = false;
          this.sidebarWidth = clampSidebarWidth(SIDEBAR_DEFAULT);
        } else {
          this.sidebarWidth = clampSidebarWidth(this.sidebarWidth + step);
        }
        this.persistSidebar();
      } else if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        this.toggleSidebarCollapsed();
      }
    },
  },
};
</script>

<style scoped>
.mobile-shell {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.mobile-shell-main {
  position: relative;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding-left: env(safe-area-inset-left);
  padding-right: env(safe-area-inset-right);
  background: var(--m-page, #f2f2f7);
}

.mobile-shell-main-tabs {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.mobile-shell-main.has-todo-detail {
  background: var(--m-surface, #fff);
}

/* Phone: nav at bottom via order */
.mobile-shell:not(.is-desktop) .mobile-shell-nav {
  order: 2;
  padding-top: 6px;
  padding-bottom: max(6px, env(safe-area-inset-bottom));
  border-top: 1px solid var(--m-hairline, rgba(60, 60, 67, 0.18));
}

.mobile-shell:not(.is-desktop) .mobile-shell-main {
  order: 1;
  padding-top: env(safe-area-inset-top);
}

.mobile-shell:not(.is-desktop) .mobile-shell-collapse,
.mobile-shell:not(.is-desktop) .mobile-shell-resizer {
  display: none;
}

.mobile-shell-nav {
  display: flex;
  justify-content: space-around;
  align-items: stretch;
  padding-left: env(safe-area-inset-left);
  padding-right: env(safe-area-inset-right);
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: saturate(180%) blur(16px);
  -webkit-backdrop-filter: saturate(180%) blur(16px);
  flex-shrink: 0;
  z-index: 2;
}

.dark-theme .mobile-shell-nav {
  background: rgba(28, 28, 30, 0.92);
}

.mobile-shell-nav button {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  border: none;
  background: transparent;
  min-height: 49px;
  padding: 4px 6px 2px;
  font-size: var(--m-nav, 11px);
  font-weight: 500;
  line-height: 1.15;
  color: var(--m-muted, rgba(60, 60, 67, 0.6));
  cursor: pointer;
}

.mobile-shell-nav button i {
  font-size: 22px;
  line-height: 1;
}

.mobile-shell-nav button.active {
  color: #0d6efd;
  font-weight: 600;
}

.dark-theme .mobile-shell-nav button.active {
  color: #0a84ff;
}

/* Desktop / wide browser: left sidebar */
.mobile-shell.is-desktop {
  flex-direction: row;
  align-items: stretch;
}

.mobile-shell.is-desktop.sidebar-resizing {
  cursor: col-resize;
  user-select: none;
}

.mobile-shell.is-desktop.sidebar-resizing * {
  cursor: col-resize !important;
}

.mobile-shell.is-desktop .mobile-shell-nav {
  order: 0;
  position: relative;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  gap: 4px;
  flex: 0 0 auto;
  min-width: 0;
  padding: 16px 12px 12px;
  border-right: 1px solid var(--m-hairline, rgba(60, 60, 67, 0.18));
  border-bottom: none;
  border-top: none;
  transition: width 0.15s ease, flex-basis 0.15s ease;
}

.mobile-shell.is-desktop.sidebar-resizing .mobile-shell-nav {
  transition: none;
}

.mobile-shell.is-desktop .mobile-shell-main {
  order: 1;
  flex: 1 1 auto;
  min-width: 0;
  padding-top: 0;
}

.mobile-shell.is-desktop .mobile-shell-nav > button:not(.mobile-shell-collapse) {
  flex: 0 0 auto;
  flex-direction: row;
  justify-content: flex-start;
  gap: 10px;
  min-height: 40px;
  width: 100%;
  padding: 10px 12px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
}

.mobile-shell.is-desktop .mobile-shell-nav button i {
  font-size: 18px;
  width: 1.25em;
  text-align: center;
  flex: 0 0 auto;
}

.mobile-shell.is-desktop .mobile-shell-nav button.active {
  background: rgba(13, 110, 253, 0.12);
}

.dark-theme .mobile-shell.is-desktop .mobile-shell-nav button.active {
  background: rgba(10, 132, 255, 0.22);
}

.mobile-shell.is-desktop .mobile-shell-collapse {
  flex: 0 0 auto;
  flex-direction: row;
  justify-content: flex-start;
  gap: 10px;
  width: 100%;
  min-height: 36px;
  margin-top: auto;
  padding: 8px 12px;
  border-radius: 10px;
  font-size: 13px;
  color: var(--m-muted, rgba(60, 60, 67, 0.6));
}

.mobile-shell.is-desktop .mobile-shell-resizer {
  position: absolute;
  top: 0;
  right: -3px;
  width: 6px;
  height: 100%;
  cursor: col-resize;
  z-index: 3;
  touch-action: none;
}

.mobile-shell.is-desktop .mobile-shell-resizer::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 1px;
  width: 2px;
  height: 28px;
  margin-top: -14px;
  border-radius: 1px;
  background: rgba(60, 60, 67, 0.22);
  opacity: 0;
  transition: opacity 0.15s ease;
}

.mobile-shell.is-desktop .mobile-shell-resizer:hover::after,
.mobile-shell.is-desktop .mobile-shell-resizer:focus-visible::after,
.mobile-shell.is-desktop.sidebar-resizing .mobile-shell-resizer::after {
  opacity: 1;
  background: #0d6efd;
}

/* Collapsed: icon rail */
.mobile-shell.is-desktop.sidebar-collapsed .mobile-shell-nav {
  padding-left: 8px;
  padding-right: 8px;
  align-items: center;
}

.mobile-shell.is-desktop.sidebar-collapsed .mobile-shell-nav-label {
  display: none;
}

.mobile-shell.is-desktop.sidebar-collapsed .mobile-shell-nav > button:not(.mobile-shell-collapse),
.mobile-shell.is-desktop.sidebar-collapsed .mobile-shell-collapse {
  justify-content: center;
  padding-left: 0;
  padding-right: 0;
  gap: 0;
}

.mobile-shell.is-desktop.sidebar-collapsed .mobile-shell-nav button i {
  width: auto;
}
</style>

<style lang="scss">
@import "./mobile.scss";
</style>
